import React, { useEffect, useState } from 'react';
import { checkValuesAgainstBoundaries, getTrackBackground, Range } from 'react-range';
import styled from 'styled-components';

import { dropDecimals } from '../../../helpers/common';

const InputGroup = styled.div`
  display: flex;
  padding: 0 5px;
  margin-bottom: 25px;
`;

const Input = styled.input`
  width: 100%;
  margin-right: 5px;
`;

const TrackContainer = styled.div`
  height: 36px;
  padding: 15px;
  display: flex;
  width: 100%;
`;

const ThumbContainer = styled.div`
  height: 10px;
  width: 10px;
  border-radius: 4px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 6px #aaa;
`;

const ThumbNumber = styled.div`
  position: absolute;
  top: -28px;
  color: #1e388b;
  font-weight: bold;
  font-size: 10px;
  font-family: Arial, Helvetica Neue, Helvetica, sans-serif;
  padding: 4px;
  border-radius: 4px;
  background-color: #ccd8ff;
`;

const AppSlider = ({ step, values, min, max, onChange, reset }) => {
  const [_values, _setValues] = useState(values);

  useEffect(() => {
    const newValues = [...values].map((value) => checkValuesAgainstBoundaries(value, min, max));
    _setValues(newValues);
  }, [values, step, min, max]);

  useEffect(() => {
    if (reset) {
      _setValues(values);
    }
  }, [reset, values]);

  const processValue = (value, index = null) => {
    if (!value.trim() || isNaN(value)) {
      return 0;
    }

    let newVal = step === 1 ? parseInt(value) : dropDecimals(value, 2);
    newVal = newVal > max ? max : newVal;
    newVal = newVal < min ? min : newVal;

    if (index !== null && values[0] !== _values[0] && values[1] !== _values[1]) {
      if (index === 0) {
        newVal = Math.min(newVal, _values[1]);
      } else {
        newVal = Math.max(newVal, _values[0]);
      }
    }

    return newVal;
  };

  const handleBlur = (e, index) => {
    const newValue = processValue(e.target.value, index);
    const newValues = index === 0 ? [newValue, _values[1]] : [_values[0], newValue];
    _setValues(newValues);
    onChange(newValues);
  };

  return (
    <>
      <InputGroup>
        <Input
          className={min !== max ? "input-small" : "input-small__disabled"}
          type="text"
          value={_values[0]}
          onChange={(e) => _setValues([processValue(e.target.value), _values[1]])}
          onBlur={(e) => handleBlur(e, 0)}
          disabled={min === max}
        />
        <Input
          className={min !== max ? "input-small" : "input-small__disabled"}
          type="text"
          value={_values[1]}
          onChange={(e) => _setValues([_values[0], processValue(e.target.value)])}
          onBlur={(e) => handleBlur(e, 1)}
          disabled={min === max}
        />
      </InputGroup>
      {
        (min !== max) &&
        <Range
          step={step}
          min={min}
          max={max}
          values={_values}
          onChange={_setValues}
          onFinalChange={onChange}
          renderTrack={({props, children}) => (
            <TrackContainer
              onTouchStart={props.onTouchStart}
              style={{...props.style}}>
              <div
                ref={props.ref}
                style={{
                  height: '5px',
                  width: '100%',
                  borderRadius: '4px',
                  background: getTrackBackground({
                    values: _values,
                    colors: ['#ccc', '#CCD8FF', '#ccc'],
                    min: min,
                    max: max,
                  }),
                  alignSelf: 'center',
                }}>
                {children}
              </div>
            </TrackContainer>
          )}
          renderThumb={({index, props}) => (
            <ThumbContainer {...props} style={{...props.style}}>
              <ThumbNumber>{_values[index]}</ThumbNumber>
            </ThumbContainer>
          )}
        />}
    </>
  );
};

export default AppSlider;